import axios from 'axios';

const API_URL = window.REACT_APP_CREWBANK_API_URL || 'https://api.' + window.BASE_DOMAIN;

export const getUserProfile = async (token) => {
  try {
    const response = await axios.get(`${API_URL}/api/user/profile`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
};

export const createOrUpdateProfile = async (token, profileData) => {
  try {    
    const response = await axios.post(`${API_URL}/api/user/profile`, profileData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error creating or updating user profile:', error);
    throw error;
  }
};

const resizeAndConvertImage = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 512;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, 512, 512);
        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/jpeg', 0.9);
      };
      img.onerror = reject;
      img.src = event.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const submitProfilePicture = async (token, profilePicture) => {
  try {
    const resizedImage = await resizeAndConvertImage(profilePicture);
    
    const formData = new FormData();
    formData.append('profilePicture', resizedImage, 'profile.jpg');

    const response = await axios.post(`${API_URL}/api/user/profile-picture`, formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading profile picture:', error);
    throw error;
  }
};


export const submitBoatPicture = async (token, boatPicture) => {
  try {
    const resizedImage = await resizeAndConvertImage(boatPicture);
    
    const formData = new FormData();
    formData.append('boatPicture', resizedImage, 'boat.jpg');

    const response = await axios.post(`${API_URL}/api/user/boat-picture`, formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading boat picture:', error);
    throw error;
  }
};


export const loadBoatPicture = async (profile) => {
  try {
    const response = await axios.get(profile.boatPictureUrl, {
      responseType: 'blob'
    });
    return URL.createObjectURL(response.data);
  } catch (error) {
    console.error('Error loading boat picture:', error);
    return null;
  }
};

export const loadProfilePicture = async (profile) => {
  try {
    const response = await axios.get(profile.profilePictureUrl, {
      responseType: 'blob'
    });
    return URL.createObjectURL(response.data);
  } catch (error) {
    console.error('Error loading profile picture:', error);
    return null;
  }
};

export const isProfileEmpty = (profile) => {
  if (!profile) return true;
  
  // Check if any of the main profile fields are filled
  const hasMainFields = profile.firstName || profile.lastName || profile.role;
  
  // Check if any steps have been completed
  const hasCompletedSteps = profile.completedSteps && Object.values(profile.completedSteps).some(step => step === true);
  
  return !hasMainFields && !hasCompletedSteps;
};

// New methods for sailing opportunities

export const addSailingOpportunity = async (token, opportunityData) => {
  try {
    const response = await axios.post(`${API_URL}/api/opportunity/add`, opportunityData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error adding sailing opportunity:', error);
    throw error;
  }
};

export const editOpportunity = async (token, opportunityId, opportunityData) => {
  try {
    const response = await axios.put(`${API_URL}/api/opportunity/edit/${opportunityId}`, opportunityData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error editing opportunity:', error);
    throw error;
  }
};

export const deactivateOpportunity = async (token, opportunityId) => {
  try {
    const response = await axios.put(`${API_URL}/api/opportunity/deactivate/${opportunityId}`, {}, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error deactivating opportunity:', error);
    throw error;
  }
};

export const activateOpportunity = async (token, opportunityId) => {
  try {
    const response = await axios.put(`${API_URL}/api/opportunity/activate/${opportunityId}`, {}, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error deactivating opportunity:', error);
    throw error;
  }
};


export const deleteOpportunity = async (token, opportunityId) => {
  try {
    await axios.delete(`${API_URL}/api/opportunity/${opportunityId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });    
  } catch (error) {
    console.error('Error deactivating opportunity:', error);
    throw error;
  }
};

export const getPlace = async (id) => {
  try {
    const url = new URL(`${API_URL}/api/common/place/${id}`);
    const response = await axios.get(url.toString());
    return response.data;
  } catch (error) {
    console.error('Error loading locations:', error);
    throw error;
  }
};

export const getPlaces = async (domain) => {
  try {
    const url = new URL(`${API_URL}/api/common/places/${domain}`);
    const response = await axios.get(url.toString());
    return response.data;
  } catch (error) {
    console.error('Error loading locations:', error);
    throw error;
  }
};

export const getDomains = async () => {
  try {
    const url = new URL(`${API_URL}/api/common/cities`);
    const response = await axios.get(url.toString());
    return response.data;
  } catch (error) {
    console.error('Error loading locations:', error);
    throw error;
  }
};

export const getDomain = async (domain) => {
  try {
    const url = new URL(`${API_URL}/api/common/city/${domain}`);
    const response = await axios.get(url.toString());
    return response.data;
  } catch (error) {
    console.error('Error loading locations:', error);
    throw error;
  }
};



export const getPlacesNearby = async (token, placeId, distanceKm, excludeCurrent = true) => {
  try {
    const url = new URL(`${API_URL}/api/common/placesnearby?placeId=${placeId}&distance=${distanceKm}&excludeCurrent=${excludeCurrent}`);
    const response = await axios.get(url.toString(), {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error loading locations:', error);
    throw error;
  }
};

export const getOpportunity = async (token, opportunityId) => {
  try {
    const response = await axios.get(`${API_URL}/api/opportunity/${opportunityId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting opportunity:', error);
    throw error;
  }
};

export const getUserOpportunities = async (token, isActive) => {
  try {
    const url = new URL(`${API_URL}/api/opportunity/my`);
    if (isActive !== undefined) {
      url.searchParams.append('isActive', isActive);
    }
    const response = await axios.get(url.toString(), {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    console.log(response.data);
    return response.data;
  } catch (error) {
    console.error('Error getting user opportunities:', error);
    throw error;
  }
};

export const getMyMatches = async (token) => {
  try {
    const url = new URL(`${API_URL}/api/matches/my`);
    const response = await axios.get(url.toString(), {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting user matches:', error);
    throw error;
  }
};

export const createMatch = async (token, oppId, answer) => {
    try {
      const url = new URL(`${API_URL}/api/matches/create/${oppId}/${answer}`);
      const response = await axios.post(url.toString(), {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data.match;
    } catch (error) {
      console.error('Error creating a match: ', error);
      throw error;
    }
  };


export const matchAnswer = async (token, oppId, answer, ) => {
  try {
    const url = new URL(`${API_URL}/api/matches/answer/${oppId}/${answer}`);
    const response = await axios.put(url.toString(), {}, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data.match;
  } catch (error) {
    console.error('Error submitting answer: ', error);
    throw error;
  }
};

export const deleteProfile = async (token) => {
  try {
    await axios.delete(`${API_URL}/api/user/profile`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  } catch (error) {
    console.error('Error deleting profile:', error);
    throw error;
  }
};

export const getPublicProfile = async (token, userId) => {
  try {
    const response = await axios.get(`${API_URL}/api/user/profile/${userId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting public profile:', error);
    throw error;
  }
};

export const sendTestEmail = async (token) => {
  try {
    const response = await axios.post(`${API_URL}/api/user/test-email`, {}, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error sending test email:', error);
    throw error;
  }
};

export const getOpportunitiesByDomain = async (token, domain) => {
  try {
    const response = await axios.get(`${API_URL}/api/opportunity/domain/${domain}/list?excludeMatches=true`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting opportunities by domain:', error);
    throw error;
  }
};

export const getOpportunitiesByPlace = async (token, placeId, excludeMatches) => {
  try {
    const response = await axios.get(`${API_URL}/api/opportunity/place/${placeId}/list?excludeMatches=${excludeMatches}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting opportunities by place:', error);
    throw error;
  }
};

// Public Roster API calls
export const getPublicRoster = async (token, domain, filters = {}) => {
  try {
    const url = new URL(`${API_URL}/api/roster/${domain}`);
    if (filters.day) url.searchParams.append('day', filters.day);
    if (filters.timeSlot) url.searchParams.append('timeSlot', filters.timeSlot);
    if (filters.activity) url.searchParams.append('activity', filters.activity);
    
    const response = await axios.get(url.toString(), {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting public roster:', error);
    throw error;
  }
};

// Invitation API calls
export const createInvitation = async (token, opportunityId, crewUserId) => {
  try {
    const response = await axios.post(`${API_URL}/api/invitation/create`, {
      opportunityId,
      crewUserId
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error creating invitation:', error);
    throw error;
  }
};

export const withdrawInvitation = async (token, invitationId) => {
  try {
    const response = await axios.delete(`${API_URL}/api/invitation/${invitationId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error withdrawing invitation:', error);
    throw error;
  }
};

export const getOpportunityInvitations = async (token, opportunityId) => {
  try {
    const response = await axios.get(`${API_URL}/api/invitation/opportunity/${opportunityId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting opportunity invitations:', error);
    throw error;
  }
};

// Messaging API calls
export const getConversations = async (token) => {
  try {
    const response = await axios.get(`${API_URL}/api/messages/conversations`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting conversations:', error);
    throw error;
  }
};

export const getMessageThread = async (token, otherUserId) => {
  try {
    const response = await axios.get(`${API_URL}/api/messages/with/${otherUserId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting message thread:', error);
    throw error;
  }
};

export const sendMessage = async (token, recipientId, body) => {
  try {
    const response = await axios.post(`${API_URL}/api/messages/send`, {
      recipientId,
      body
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

export const getUnreadMessageCount = async (token) => {
  try {
    const response = await axios.get(`${API_URL}/api/messages/unread-count`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data.count;
  } catch (error) {
    console.error('Error getting unread message count:', error);
    throw error;
  }
};
